import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";

import api, { formatApiError, fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";


export default function ProductForm() {
    const { t, lang } = useLang();
    const { id } = useParams();
    const navigate = useNavigate();

    const isEdit = Boolean(id && id !== "new");

    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        title: "",
        description: "",
        price: 0,
        discount_price: null,
        stock: 1,
        category_id: "",
        images: [],
        video_url: "",
        ingredients: "",
        allergens: "",
        tags: [],
        weight: "",
        prep_time: "",
    });

    useEffect(() => {
        api.get("/categories")
            .then((response) => {
                setCategories(response.data);
            })
            .catch(() => {
                toast.error(
                    lang === "ar"
                        ? "تعذر تحميل التصنيفات"
                        : "Could not load categories"
                );
            });

        if (isEdit) {
            api.get(`/products/${id}`)
                .then((response) => {
                    setForm({
                        ...response.data.product,
                        images: response.data.product.images || [],
                        tags: response.data.product.tags || [],
                    });
                })
                .catch((error) => {
                    toast.error(
                        formatApiError(
                            error.response?.data?.detail
                        ) ||
                        (
                            lang === "ar"
                                ? "تعذر تحميل بيانات المنتج"
                                : "Could not load product"
                        )
                    );
                });
        }
    }, [id, isEdit, lang]);

    const onSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);

        try {
            const payload = {
                ...form,
                price: parseFloat(form.price),
                stock: parseInt(form.stock, 10),
            };

            if (
                payload.discount_price !== null &&
                payload.discount_price !== ""
            ) {
                payload.discount_price = parseFloat(
                    payload.discount_price
                );
            } else {
                payload.discount_price = null;
            }

            if (isEdit) {
                await api.put(`/products/${id}`, payload);
            } else {
                await api.post("/products", payload);
            }

            toast.success(`${t("save")} ✓`);
            navigate("/seller/products");
        } catch (error) {
            toast.error(
                formatApiError(
                    error.response?.data?.detail
                ) || error.message
            );
        } finally {
            setSaving(false);
        }
    };

    const uploadImage = async (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            const { data } = await api.post(
                "/uploads/image",
                formData
            );

            setForm((currentForm) => ({
                ...currentForm,
                images: [
                    ...(currentForm.images || []),
                    data.url,
                ],
            }));

            toast.success(
                lang === "ar"
                    ? "تم رفع الصورة بنجاح"
                    : "Image uploaded successfully"
            );
        } catch (error) {
            toast.error(
                formatApiError(
                    error.response?.data?.detail
                ) ||
                (
                    lang === "ar"
                        ? "فشل رفع الصورة"
                        : "Image upload failed"
                )
            );
        } finally {
            event.target.value = "";
        }
    };

    const removeImage = (imageIndex) => {
        setForm((currentForm) => ({
            ...currentForm,
            images: (currentForm.images || []).filter(
                (_, index) => index !== imageIndex
            ),
        }));
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
                <h1 className="text-3xl font-black text-espresso">
                    {isEdit
                        ? t("editProduct")
                        : t("addProduct")}
                </h1>

                <form
                    onSubmit={onSubmit}
                    className="mt-6 bg-white border border-border rounded-3xl p-6 md:p-8 space-y-5"
                    data-testid="product-form"
                >
                    <div>
                        <Label htmlFor="product-title">
                            {t("title")}
                        </Label>

                        <Input
                            id="product-title"
                            required
                            value={form.title}
                            onChange={(event) =>
                                setForm({
                                    ...form,
                                    title: event.target.value,
                                })
                            }
                            className="mt-1.5"
                            data-testid="product-title-input"
                        />
                    </div>

                    <div>
                        <Label htmlFor="product-description">
                            {t("description")}
                        </Label>

                        <Textarea
                            id="product-description"
                            required
                            rows={5}
                            value={form.description}
                            onChange={(event) =>
                                setForm({
                                    ...form,
                                    description:
                                        event.target.value,
                                })
                            }
                            className="mt-1.5"
                            data-testid="product-desc-input"
                        />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <Label htmlFor="product-price">
                                {t("price")} ({t("SAR")})
                            </Label>

                            <Input
                                id="product-price"
                                required
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.price}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        price: event.target.value,
                                    })
                                }
                                className="mt-1.5"
                                data-testid="product-price-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="discount-price">
                                {t("discountPrice")}
                            </Label>

                            <Input
                                id="discount-price"
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.discount_price ?? ""}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        discount_price:
                                            event.target.value,
                                    })
                                }
                                className="mt-1.5"
                            />
                        </div>

                        <div>
                            <Label htmlFor="product-stock">
                                {t("stock")}
                            </Label>

                            <Input
                                id="product-stock"
                                required
                                type="number"
                                min="0"
                                value={form.stock}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        stock: event.target.value,
                                    })
                                }
                                className="mt-1.5"
                                data-testid="product-stock-input"
                            />
                        </div>
                    </div>

                    <div>
                        <Label>
                            {t("filterByCategory")}
                        </Label>

                        <Select
                            value={form.category_id}
                            onValueChange={(value) =>
                                setForm({
                                    ...form,
                                    category_id: value,
                                })
                            }
                        >
                            <SelectTrigger
                                className="mt-1.5"
                                data-testid="product-category-select"
                            >
                                <SelectValue placeholder="—" />
                            </SelectTrigger>

                            <SelectContent>
                                {categories.map((category) => (
                                    <SelectItem
                                        key={category.category_id}
                                        value={category.category_id}
                                    >
                                        {lang === "ar"
                                            ? category.name_ar
                                            : category.name_en}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label>
                            {t("images")}
                        </Label>

                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                            {(form.images || []).map(
                                (image, index) => (
                                    <div
                                        key={`${image}-${index}`}
                                        className="relative aspect-square rounded-xl overflow-hidden border border-border group"
                                        data-testid={`product-image-${index}`}
                                    >
                                        <img
                                            src={fileUrl(image)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />

                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeImage(index)
                                                }
                                                className="bg-destructive text-white px-3 py-2 rounded-full"
                                                aria-label={
                                                    lang === "ar"
                                                        ? "حذف الصورة"
                                                        : "Remove image"
                                                }
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                )
                            )}

                            <label className="aspect-square rounded-xl border-2 border-dashed border-border grid place-items-center cursor-pointer hover:border-terracotta transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={uploadImage}
                                    className="hidden"
                                    data-testid="upload-image-input"
                                />

                                <div className="text-center text-xs text-muted-foreground">
                                    <div className="text-2xl">
                                        +
                                    </div>
                                    {t("upload")}
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="ingredients">
                                {t("ingredients")}
                            </Label>

                            <Textarea
                                id="ingredients"
                                rows={2}
                                value={form.ingredients || ""}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        ingredients:
                                            event.target.value,
                                    })
                                }
                                className="mt-1.5"
                            />
                        </div>

                        <div>
                            <Label htmlFor="allergens">
                                {t("allergens")}
                            </Label>

                            <Textarea
                                id="allergens"
                                rows={2}
                                value={form.allergens || ""}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        allergens:
                                            event.target.value,
                                    })
                                }
                                className="mt-1.5"
                            />
                        </div>
                    </div>


                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="product-weight">
                                {t("weight")}
                            </Label>

                            <Input
                                id="product-weight"
                                value={form.weight || ""}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        weight: event.target.value,
                                    })
                                }
                                className="mt-1.5"
                                placeholder={
                                    lang === "ar"
                                        ? "500 جم"
                                        : "500 g"
                                }
                                data-testid="product-weight-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="prep-time">
                                {t("prepTime")}
                            </Label>

                            <Input
                                id="prep-time"
                                value={form.prep_time || ""}
                                onChange={(event) =>
                                    setForm({
                                        ...form,
                                        prep_time:
                                            event.target.value,
                                    })
                                }
                                className="mt-1.5"
                                placeholder={
                                    lang === "ar"
                                        ? "24 ساعة"
                                        : "24 hours"
                                }
                                data-testid="product-preptime-input"
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={saving}
                        className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                        data-testid="save-product-btn"
                    >
                        {saving ? "..." : t("save")}
                    </Button>
                </form>
            </div>
        </Layout>
    );
}
